import React, { useState, useEffect, FC, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useFormSteps } from "../../hooks/useFormSteps";
import {
  setCompanyRegistrationSubmitted,
  getCompanyStatus,
  SimplifiedCompanyStatus,
} from "../../utils/authUtils";
import { useCompany } from "../../context/CompanyContext";
import { useAuth } from "../../context/AuthContext"; // Import useAuth hook
import Stepper from "../../components/ui/Stepper";
import Button from "../../components/ui/Button";
import {
  CompanyBasicInfo,
  ContactInformation,
  BusinessDetails,
  LegalDocuments,
  ReviewAndSubmit,
} from "../../components/company/CompanyRegistrationSteps";
import {
  CompanyRegistrationFormData,
  ProfileFormErrors,
  StepDefinition,
  CompanyProfileData, // Import CompanyProfileData
} from "../../types/components";
import { CompanyStatus } from "../../types/localStorage";
import Toast from "../../components/ui/Toast";

const RegisterCompany: FC = () => {
  const navigate = useNavigate();
  const { companyDetails,  loadingState, updateCompanyDetails, errorState } = useCompany(); // Use useCompany hook
  const { user } = useAuth(); // Use useAuth hook
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [stepErrors, setStepErrors] = useState<ProfileFormErrors>({});
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' | 'warning' | 'info' } | null>(null);

  const initialFormDataRef = useRef<CompanyRegistrationFormData | null>(null); // Ref to store initial form data

  const stepFields: { [key: number]: Array<keyof CompanyRegistrationFormData | 'forceApiCall'> } = {
    0: ['companyName', 'website', 'description'],
    1: ['contactName', 'contactEmail', 'contactPhone', 'address'],
    2: ['industry', 'companySize', 'businessType', 'foundedYear', 'forceApiCall'],
    3: ['registrationType', 'gstNumber', 'panNumber', 'msmeNumber', 'gstCertificate', 'businessLicense', 'panCard', 'msmeCertificate', 'businessRegistration'],
    // Step 4 (Review & Submit) will handle all data, so no specific fields here.
  };

  // On mount, detect if company registration is already submitted/pending or approved
  useEffect(() => {
    console.log("RegisterCompany use effect", loadingState , companyDetails)
    if (loadingState.companyDetails) return; // Wait for company details to load

    const status: SimplifiedCompanyStatus = getCompanyStatus(companyDetails); // Pass companyDetails
    if (status.status === "Pending") {
      // setIsSubmitted(true);
    }
    if (status.status === "Verified" ) {
      // If approved, redirect to dashboard instead of showing form
      navigate("/company/dashboard");
    } else if (status.status === "Rejected") {
      // If rejected, allow re-registration
      // setIsSubmitted(false);
    }
  }, [navigate, companyDetails, loadingState.companyDetails]); // Add companyDetails and loadingState to dependencies

  const horizontalProgressBarRef = useRef<HTMLDivElement>(null);

  const steps: StepDefinition[] = [
    { title: "Company Info", description: "Basic company information" },
    { title: "Contact Details", description: "Contact information" },
    { title: "Business Details", description: "Industry and size" },
    { title: "Legal Documents", description: "GST, PAN, and documents" },
    { title: "Review & Submit", description: "Final review" },
  ];

  const initialFormState: CompanyRegistrationFormData = { // Renamed to avoid conflict
    companyName: "",
    website: undefined,
    description: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    address: "",
    industry: "",
    companySize: "",
    businessType: "",
    foundedYear: undefined,
    registrationType: 'gstin',
    gstNumber: "",
    panNumber: "",
    msmeNumber: "",
    gstCertificate: null,
    businessLicense: null,
    panCard: null,
    msmeCertificate: null,
    businessRegistration: null,
  };

  const {
    currentStep,
    formData,
    nextStep,
    prevStep,
    goToStep,
    updateFormData,
  } = useFormSteps<CompanyRegistrationFormData>(steps, initialFormState); // Use initialFormState

  // Populate form data from companyDetails if available
  useEffect(() => {
    if (companyDetails && !loadingState.companyDetails) {
      const profileData: CompanyProfileData = {
        companyName: companyDetails.companyName || '',
        companyId: companyDetails.companyId || '',
        gstNumber: companyDetails.gstin || '',
        industry: companyDetails.industry || '',
        companySize: companyDetails.empStrength || '',
        website: companyDetails.webpageUrl || '',
        description: companyDetails.description || '',
        address: companyDetails.address || '',
        isVerified: companyDetails.verificationStatus === 'Verified',
        verificationStatus: companyDetails.verificationStatus.toLowerCase() || 'pending',
        panNumber: '',
        msmeNumber: '',
        registrationType: 'gstin',
        registrationNumber: '',
        businessType: '',
        foundedYear: '',
        city: '',
        state: '',
        pincode: '',
        country: '',
        contactPerson: '',
        contactEmail: '',
        contactPhone: '',
        hrPerson: '',
        hrEmail: '',
        hrPhone: '',
        linkedinUrl: '',
        twitterUrl: '',
        facebookUrl: '',
        instagramUrl: '',
        attachments: []
      };

      if (companyDetails.otherDetails) {
        try {
          const outerParsedDetails = JSON.parse(companyDetails.otherDetails);
          profileData.panNumber = outerParsedDetails.panNumber || profileData.panNumber;
          profileData.registrationNumber = outerParsedDetails.registrationNumber || profileData.registrationNumber;
          profileData.businessType = outerParsedDetails.businessType || profileData.businessType;
          profileData.foundedYear = outerParsedDetails.foundedYear || profileData.foundedYear;
          profileData.city = outerParsedDetails.city || profileData.city;
          profileData.state = outerParsedDetails.state || profileData.state;
          profileData.pincode = outerParsedDetails.pincode || profileData.pincode;
          profileData.country = outerParsedDetails.country || profileData.country;
          profileData.contactPerson = outerParsedDetails.contactPerson || profileData.contactPerson;
          profileData.contactEmail = outerParsedDetails.contactEmail || profileData.contactEmail;
          profileData.contactPhone = outerParsedDetails.contactPhone || profileData.contactPhone;
          profileData.hrEmail = outerParsedDetails.hrEmail || profileData.hrEmail;
          profileData.hrPhone = outerParsedDetails.hrPhone || profileData.hrPhone;
          profileData.linkedinUrl = outerParsedDetails.linkedinUrl || profileData.linkedinUrl;
          profileData.twitterUrl = outerParsedDetails.twitterUrl || profileData.twitterUrl;
          profileData.facebookUrl = outerParsedDetails.facebookUrl || profileData.facebookUrl;
          profileData.facebookUrl = outerParsedDetails.facebookUrl || profileData.facebookUrl;
          profileData.facebookUrl = outerParsedDetails.facebookUrl || profileData.facebookUrl;
          profileData.msmeNumber = outerParsedDetails.msmeNumber || profileData.msmeNumber;
          profileData.registrationType = outerParsedDetails.registrationType || profileData.registrationType;
          // profileData.logo = outerParsedDetails.logo || profileData.logo;
          profileData.registrationStep = outerParsedDetails.registrationStep || profileData.registrationStep;
        } catch (outerError) {
          console.error("Failed to parse outer otherDetails JSON:", outerError);
        }
      }

      // Map CompanyProfileData to CompanyRegistrationFormData
      const mappedFormData: CompanyRegistrationFormData = {
        companyName: profileData.companyName,
        website: profileData.website,
        description: profileData.description,
        contactName: profileData.contactPerson,
        contactEmail: profileData.contactEmail,
        contactPhone: profileData.contactPhone,
        address: profileData.address,
        industry: profileData.industry,
        companySize: profileData.companySize,
        businessType: profileData.businessType,
        foundedYear: profileData.foundedYear ? parseInt(profileData.foundedYear) : undefined,
        gstNumber: profileData.gstNumber,
        panNumber: profileData.panNumber,
        gstCertificate: null,
        msmeNumber: profileData.msmeNumber,
        businessLicense: null,
        panCard: null,
        msmeCertificate: null,
        businessRegistration: null,
        registrationType: profileData.registrationType, // Derive if possible, or leave undefined
      };
      updateFormData(mappedFormData);
      initialFormDataRef.current = mappedFormData; // Set initial form data for comparison

      // Navigate to the last saved step
      if (profileData.registrationStep !== undefined) {
        goToStep(profileData.registrationStep);
      }
    }
  }, [companyDetails, loadingState.companyDetails]);

  useEffect(() => {
    if (horizontalProgressBarRef.current) {
      const currentStepElement = horizontalProgressBarRef.current.querySelector(
        `[data-step-index="${currentStep}"]`
      ) as HTMLElement;
      if (currentStepElement) {
        currentStepElement.scrollIntoView({
          behavior: "smooth",
          inline: "center",
        });
      }
    }
  }, [currentStep]);

  const handleNext = async (errors: ProfileFormErrors = {}) => {
    if (Object.keys(errors).length === 0) {
      setStepErrors({});

      // Check for changes in current step's fields
      const currentStepFields = stepFields[currentStep];
      let hasChanges = false;
      const updatedFields: Partial<CompanyProfileData> = {};

      if (initialFormDataRef.current) {
        for (const field of currentStepFields) {
          if (field === 'forceApiCall') {
            hasChanges = true;
          }
          // Special handling for foundedYear as it's number in form and string in profileData
          if (field === 'foundedYear') {
            const currentFoundedYear = formData.foundedYear?.toString() || '';
            const initialFoundedYear = initialFormDataRef.current.foundedYear?.toString() || '';
            if (currentFoundedYear !== initialFoundedYear) {
              hasChanges = true;
              (updatedFields as any)[field] = currentFoundedYear;
            }
          } 
          else if (field === 'registrationType') {
            // Special handling for registrationType if it needs to be derived
            // For now, assume direct comparison is fine if it's a simple string
            if (formData[field] !== initialFormDataRef.current[field]) {
              hasChanges = true;
              (updatedFields as any)[field] = formData[field];
            }
          }
          else if (formData[field] !== initialFormDataRef.current[field]) {
            hasChanges = true;
            (updatedFields as any)[field] = formData[field];
          }
        }
      } else {
        // If initialFormDataRef is null, it means it's a new registration, so always send data
        hasChanges = true;
      }

      if (hasChanges || currentStep === 0) {
        // Always save on first step or if changes exist
        try {
          const companyProfileData: CompanyProfileData = {
            companyName: formData.companyName,
            companyId:
              user && user.role === "Company" && user.companyId
                ? user.companyId
                : "",
            gstNumber: formData.gstNumber || "",
            panNumber: formData.panNumber || "",
            registrationType: formData.registrationType,
            msmeNumber: formData.msmeNumber || "",
            registrationNumber: "",
            businessType: formData.businessType || "",
            industry: formData.industry || "",
            companySize: formData.companySize || "",
            foundedYear: formData.foundedYear?.toString() || "",
            website: formData.website,
            description: formData.description,
            address: formData.address,
            city: "",
            state: "",
            pincode: "",
            country: "",
            contactPerson: formData.contactName,
            contactEmail: formData.contactEmail,
            contactPhone: formData.contactPhone,
            hrPerson: "",
            hrEmail: "",
            hrPhone: "",
            linkedinUrl: "",
            twitterUrl: "",
            facebookUrl: "",
            instagramUrl: "",
            attachments: [],
            isVerified: false,
            verificationStatus: "pending",
            registrationStep: currentStep + 1, // Pass next step for tracking
          };
          await updateCompanyDetails(companyProfileData);
          // await fetchCompanyDetails(companyId);
          initialFormDataRef.current = formData; // Update ref after successful save
          if (currentStep < steps.length - 1) {
            nextStep();
          }
        } catch (err: any) {
          setToast({
            message:
              err.data.message ||
              (typeof err == "string" ? err : "something went wrong"),
            type: "error",
          });
          // Handle error
        } finally {
          // setIsSubmitting(false);
        }
      } else if (currentStep < steps.length - 1) {
          nextStep();
      }
    } else {
      setStepErrors(errors);
    }
  };

  const handlePrev = () => {
    setStepErrors({});
    if (currentStep > 0) {
      prevStep();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Map CompanyRegistrationFormData to CompanyProfileData
      const companyProfileData: CompanyProfileData = {
        companyName: formData.companyName,
        companyId: (user && user.role === "Company" && user.companyId) ? user.companyId : '', // Use existing companyId or empty for new
        gstNumber: formData.gstNumber || '',
        panNumber: formData.panNumber || '',
        msmeNumber: formData.msmeNumber || '',
        registrationType: formData.registrationType,
        registrationNumber: '', // Not directly in form, will be in otherDetails
        businessType: formData.businessType || '',
        industry: formData.industry || '',
        companySize: formData.companySize || '',
        foundedYear: formData.foundedYear?.toString() || '',
        website: formData.website,
        description: formData.description,
        address: formData.address,
        city: '', // Not directly in form, will be in otherDetails
        state: '', // Not directly in form, will be in otherDetails
        pincode: '', // Not directly in form, will be in otherDetails
        country: '', // Not directly in form, will be in otherDetails
        contactPerson: formData.contactName,
        contactEmail: formData.contactEmail,
        contactPhone: formData.contactPhone,
        hrPerson: '', // Not directly in form, will be in otherDetails
        hrEmail: '', // Not directly in form, will be in otherDetails
        hrPhone: '', // Not directly in form, will be in otherDetails
        linkedinUrl: '', // Not directly in form, will be in otherDetails
        twitterUrl: '', // Not directly in form, will be in otherDetails
        facebookUrl: '', // Not directly in form, will be in otherDetails
        instagramUrl: '', // Not directly in form, will be in otherDetails
        attachments: [], // Not directly in form, will be in otherDetails
        isVerified: false, // Default
        verificationStatus: 'pending', // Default
        shouldSubmitProfile: true,
        registrationStep: currentStep, // Pass current step for tracking
      };

      await updateCompanyDetails(companyProfileData);
      setCompanyRegistrationSubmitted(); // This might need to be updated based on API response
      setIsSubmitted(true);
    } catch (error : any) {
      console.error("Registration submission failed:", error);
      setToast({message : error.data.message || (typeof error == 'string'? error : "something went wrong"), type:'error'})
      // Handle error, show toast or message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStepClick = (stepIndex: number) => {
    if (stepIndex <= currentStep) {
      goToStep(stepIndex);
      setStepErrors({});
    }
  };

  const renderStepContent = () => {
    const commonProps = {
      formData,
      updateFormData,
      errors: stepErrors,
    };

    switch (currentStep) {
      case 0:
        return <CompanyBasicInfo {...commonProps} onNext={handleNext} />;
      case 1:
        return (
          <ContactInformation
            {...commonProps}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 2:
        return (
          <BusinessDetails
            {...commonProps}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 3:
        return (
          <LegalDocuments
            {...commonProps}
            onNext={handleNext}
            onPrev={handlePrev}
          />
        );
      case 4:
        return (
          <ReviewAndSubmit
            {...commonProps}
            onPrev={handlePrev}
            onNext={handleSubmit}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
          />
        );
      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#87DE88]/10 flex items-center justify-center p-4">
        <div className="w-full max-w-6xl mx-auto">
          <div className="bg-white/95 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            {/* Enhanced Header Section */}
            <div className="bg-[#87DE88] px-6 sm:px-8 lg:px-12 py-12 sm:py-16">
              <div className="text-center">
                {/* Enhanced Success Icon */}
                <div className="relative mb-8 sm:mb-10">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 mx-auto bg-white rounded-full flex items-center justify-center shadow-2xl">
                    <svg
                      className="w-10 h-10 sm:w-12 sm:h-12 lg:w-14 lg:h-14 text-green-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={3}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  {/* Enhanced Animated rings */}
                  <div className="absolute inset-0 w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 mx-auto border-2 border-white/30 rounded-full animate-ping"></div>
                  <div className="absolute inset-0 w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 mx-auto border border-white/20 rounded-full animate-pulse"></div>
                </div>

                {/* Enhanced Main Heading */}
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-primary-black mb-6 sm:mb-8 leading-tight">
                  🎉 Registration Submitted!
                </h1>

                {/* Enhanced Subtitle */}
                <p className="text-lg sm:text-xl md:text-2xl text-primary-black/80 mb-8 sm:mb-10 max-w-4xl mx-auto leading-relaxed font-medium">
                  Your company registration has been submitted successfully. Our
                  team will review your information and get back to you within
                  24-48 hours.
                </p>

                {/* Success Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto">
                  <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-black mb-2">
                      ✓
                    </div>
                  <div className="text-sm sm:text-base text-primary-black/80">
                      Information Received
                    </div>
                  </div>
                  <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-black mb-2">
                      24-48h
                    </div>
                  <div className="text-sm sm:text-base text-primary-black/80">
                      Review Time
                    </div>
                  </div>
                  <div className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary-black mb-2">
                      📧
                    </div>
                  <div className="text-sm sm:text-base text-primary-black/80">
                      Email Notification
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Section */}
              <div className="px-6 sm:px-8 lg:px-12 py-8 sm:py-12">
              {/* What happens next section */}
              <div className="bg-[#87DE88]/10 border border-[#87DE88]/40 rounded-2xl p-6 sm:p-8 mb-8 sm:mb-10">
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-6 sm:mb-8 text-center">
                  What happens next?
                </h2>

                {/* Steps Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                  {/* Step 1 */}
                  <div className="text-center group">
                    <div className="relative mb-4 sm:mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#87DE88] text-primary-black rounded-full flex items-center justify-center mx-auto text-xl sm:text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                        1
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#87DE88] rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-primary-black"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#1f2937] mb-2 sm:mb-3">
                      Agent Review
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      Our team reviews your registration details and documents
                    </p>
                  </div>

                  {/* Step 2 */}
                  <div className="text-center group">
                    <div className="relative mb-4 sm:mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#87DE88] text-primary-black rounded-full flex items-center justify-center mx-auto text-xl sm:text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                        2
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#87DE88] rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-primary-black"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#1f2937] mb-2 sm:mb-3">
                      Verification
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      We verify your company information and contact details
                    </p>
                  </div>

                  {/* Step 3 */}
                  <div className="text-center group sm:col-span-2 lg:col-span-1">
                    <div className="relative mb-4 sm:mb-6">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#87DE88] text-primary-black rounded-full flex items-center justify-center mx-auto text-xl sm:text-2xl font-bold shadow-lg group-hover:scale-110 transition-transform duration-300">
                        3
                      </div>
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[#87DE88] rounded-full flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-primary-black"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold text-[#1f2937] mb-2 sm:mb-3">
                      Approval
                    </h3>
                    <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                      You'll receive approval within 24-48 hours via email
                    </p>
                  </div>
                </div>
              </div>

              {/* Important Notice */}
              <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400 rounded-xl p-4 sm:p-6 mb-8 sm:mb-10">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 mt-0.5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3 sm:ml-4">
                    <h3 className="text-sm sm:text-base font-bold text-yellow-800 mb-1 sm:mb-2">
                      Important Notice
                    </h3>
                    <p className="text-sm sm:text-base text-yellow-700 leading-relaxed">
                      You will receive an email notification once your company
                      is approved. After approval, you'll have full access to
                      the company dashboard and can start posting jobs.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
                <Button
                  onClick={() => navigate("/company/dashboard")}
                  className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                  size="lg"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Check Approval Status
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/")}
                  className="px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold border-2 hover:bg-gray-50 transition-all duration-300"
                  size="lg"
                >
                  <svg
                    className="w-4 h-4 sm:w-5 sm:h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                    />
                  </svg>
                  Back to Home
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {(toast) && (
        <Toast
          message={toast.message}
          type={toast.type}
          position="top-right"
          duration={2500}
          onClose={() => setToast(null)}
          show
        />
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Horizontal Progress Bar for small screens */}
        <div className="lg:hidden mb-8">
          <div
            ref={horizontalProgressBarRef}
            className="flex items-center justify-between bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-100 p-4 overflow-x-auto scrollbar-hide" // Added scrollbar-hide
          >
            {steps.map((step, index) => (
              <div
                key={index}
                data-step-index={index} // Add data attribute for easy selection
                className={`flex-shrink-0 flex flex-col items-center cursor-pointer transition-all duration-300 ease-in-out px-2 ${
                  index === currentStep ? "scale-105" : "opacity-70"
                }`}
                onClick={() => handleStepClick(index)}
              >
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                    index === currentStep
                      ? "bg-[#87DE88] text-primary-black shadow-lg scale-110"
                      : index < currentStep
                      ? "bg-[#87DE88] text-primary-black shadow-md mb-1"
                      : "bg-white text-gray-500 border-2 border-gray-300 group-hover:border-gray-400 mb-1"
                  }`}
                >
                  {index + 1}
                </div>

                <div
                  className="flex-1 min-w-0 pt-1"
                  style={{
                    // opacity: index === currentStep ? "1" : "0",
                    transition:
                      "max-width 0.3s ease-in-out, opacity 0.3s ease-in-out",
                  }}
                >
                  <h3
                    className={`text-base font-semibold mb-1 ${
                      index === currentStep
                        ? "text-primary-lightGreen"
                        : index < currentStep
                        ? "text-green-700"
                        : "text-gray-700"
                    }`}
                  >
                    {step.title}
                  </h3>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Enhanced Progress Sidebar (Vertical - hidden on small screens) */}
          <div className="hidden lg:block lg:col-span-2">
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl border border-gray-100 p-6 lg:p-8 sticky top-24">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-[#87DE88] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Registration Progress
                </h2>
                <p className="text-sm text-gray-600">
                  Complete all steps to register your company
                </p>
              </div>

              <div className="space-y-4">
                {steps.map((step, index) => (
                  <div
                    key={index}
                    className={`group relative flex items-start space-x-4 p-4 rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-md ${
                      index === currentStep
                        ? "bg-[#87DE88]/10 border-2 border-[#87DE88]/30 shadow-md"
                        : index < currentStep
                        ? "bg-[#87DE88]/10 border-2 border-[#87DE88]/30"
                        : "bg-gray-50 border-2 border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => handleStepClick(index)}
                  >
                    <div className="relative">
                      <div
                        className={`w-10 h-10 rounded-xl flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                          index === currentStep
                            ? "bg-[#87DE88] text-primary-black shadow-lg scale-110"
                            : index < currentStep
                            ? "bg-[#87DE88] text-primary-black shadow-md"
                            : "bg-white text-gray-500 border-2 border-gray-300 group-hover:border-gray-400"
                        }`}
                      >
                        {index < currentStep ? (
                          <svg
                            className="w-5 h-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      {index < steps.length - 1 && (
                        <div
                          className={`absolute top-12 left-1/2 transform -translate-x-1/2 w-0.5 h-8 ${
                            index < currentStep ? "bg-green-300" : "bg-gray-200"
                          }`}
                        ></div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0 pt-1">
                      <h3
                        className={`text-base font-semibold mb-1 ${
                          index === currentStep
                            ? "text-[#1f2937]"
                            : index < currentStep
                            ? "text-[#1f2937]"
                            : "text-gray-700"
                        }`}
                      >
                        {step.title}
                      </h3>
                      <p
                        className={`text-sm ${
                          index === currentStep
                            ? "text-[#1f2937]"
                            : index < currentStep
                            ? "text-[#1f2937]"
                            : "text-gray-500"
                        }`}
                      >
                        {step.description}
                      </p>
                    </div>
                    {index === currentStep && (
                      <div className="absolute -right-1 top-1/2 transform -translate-y-1/2">
                        <div className="w-3 h-3 bg-[#87DE88] rounded-full animate-pulse"></div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Enhanced Progress Bar */}
              <div className="mt-8 p-4 bg-[#87DE88]/10 rounded-2xl">
                <div className="flex justify-between text-sm font-medium text-gray-700 mb-3">
                  <span>Overall Progress</span>
                  <span className="text-[#1f2937]">
                    {Math.round(((currentStep + 1) / steps.length) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-[#87DE88] h-3 rounded-full transition-all duration-700 ease-out shadow-sm"
                    style={{
                      width: `${((currentStep + 1) / steps.length) * 100}%`,
                    }}
                  ></div>
                </div>
                <div className="mt-3 text-xs text-gray-600 text-center">
                  {currentStep === steps.length - 1
                    ? "Almost done! Review and submit."
                    : `${steps.length - currentStep - 1} steps remaining`}
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
              {/* Step Header */}
              <div className="bg-[#87DE88] px-8 py-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-primary-black mb-2">
                      {steps[currentStep].title}
                    </h2>
                    <p className="text-primary-black/80 text-lg">
                      {steps[currentStep].description}
                    </p>
                  </div>
                  <div className="hidden sm:flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    <span className="text-primary-black font-medium">
                      Step {currentStep + 1} of {steps.length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Step Content */}
              <div className="p-8 sm:p-12">{renderStepContent()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterCompany;
